import { NotificationData } from '../../../services/notification.service';
import { familyTagsOf } from '../../../shared/utils/family-tags.util';
import type {
  Membership,
  VehicleWithMemberships,
  VehiclesWithMembershipsResponse,
} from '../../../services/vehicle.service';
import {
  cardStatus,
  readSubscriptionPause,
  toDate,
} from '../../../shared/utils/subscription-pause.util';
import {
  formatPauseDate,
  subscriptionStatusLabel,
} from '../../../shared/utils/subscription-pause.copy';
import type { PrepaidPurchaseStatus } from '../../../models/prepaid-wash-book.models';
import {
  describePrepaidRedemptionResult,
  PrepaidRedemptionVisitLike,
  resolvePrepaidRedemptionOrderId,
} from '../../../shared/utils/prepaid-redemption-result.util';
import { coerceUsStateCode } from '../../../shared/utils/split-license-plate-for-api';
import {
  MultiEligibleConfirmRequest,
  MultiEligibleOptionCardVm,
  MultiEligibleOptionCategoryKey,
  MultiEligibleOptionCategoryVm,
  MultiEligiblePrepaidItem,
  MultiEligiblePurchaseOption,
  MultiEligibleRedeemEvent,
  MultiEligibleSelectionKind,
  MultiEligibleVehicleCardVm,
  MultiEligibleWashItemCardVm,
} from '../models/multi-eligible.models';

function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (value == null) {
      continue;
    }
    const asString = String(value).trim();
    if (asString) {
      return asString;
    }
  }
  return '';
}

function readRecord(value: unknown): Record<string, any> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, any>;
}

/** A membership card: the shop membership path or, CWS-1679, the vendor membership path. */
export function isMembershipRedeemEvent(redeemEvent: string | null | undefined): boolean {
  return redeemEvent === 'active-subscription' || redeemEvent === 'vendor-subscription';
}

export function isMultiEligibleSelectionNotification(
  notification: NotificationData | null | undefined
): notification is NotificationData {
  return notification?.type === 'multi-eligible-selection';
}

export function isPrepaidItemRedeemable(item: MultiEligiblePrepaidItem): boolean {
  if (!item.purchase_item_id || !item.package_id) {
    return false;
  }
  if (item.remaining != null && item.remaining <= 0) {
    return false;
  }
  return true;
}

export function listRedeemablePrepaidItems(
  option: MultiEligiblePurchaseOption | null | undefined
): MultiEligiblePrepaidItem[] {
  return (option?.items ?? []).filter(isPrepaidItemRedeemable);
}

export function optionNeedsWashItemStep(
  option: MultiEligiblePurchaseOption | null | undefined
): boolean {
  if (!option || option.redeem_event !== 'redeem-prepaid-purchase') {
    return false;
  }
  return listRedeemablePrepaidItems(option).length > 1;
}

/** Prepaid packages always expand washbook details under Choose Package (Figma). */
export function optionIsPrepaidPurchase(
  option: MultiEligiblePurchaseOption | null | undefined
): boolean {
  return option?.redeem_event === 'redeem-prepaid-purchase';
}

export function readMultiEligiblePurchaseOptions(
  data: Record<string, any> | null | undefined
): MultiEligiblePurchaseOption[] {
  const row = readRecord(data);
  if (!row) {
    return [];
  }
  const raw = row['purchase_options'];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(option => readRecord(option))
    .filter((option): option is Record<string, any> => Boolean(option))
    .map(option => {
      const redeemEvent = pickString(option['redeem_event']) as MultiEligibleRedeemEvent;
      return {
        source: pickString(option['source']) || 'shop',
        redeem_event: redeemEvent,
        purchase_type: pickString(option['purchase_type']) || null,
        order_id: pickString(option['order_id']) || null,
        order_number: pickString(option['order_number']) || null,
        package_id: pickString(option['package_id']) || null,
        package_name: pickString(option['package_name']) || null,
        subscription_id: pickString(option['subscription_id']) || null,
        subscription_source: pickString(option['subscription_source']) || null,
        customer_id: pickString(option['customer_id']) || null,
        customer_name: pickString(option['customer_name']) || null,
        vehicle_id: pickString(option['vehicle_id']) || null,
        prepaid_purchase_id: pickString(option['prepaid_purchase_id']) || null,
        status: pickString(option['status']) || null,
        washes_included:
          option['washes_included'] == null ? null : Number(option['washes_included']),
        washes_redeemed:
          option['washes_redeemed'] == null ? null : Number(option['washes_redeemed']),
        last_washed_date: pickString(option['last_washed_date']) || null,
        family_group_id: pickString(option['family_group_id']) || null,
        is_family: option['is_family'] === true,
        is_primary: option['is_primary'] === true,
        pause: readSubscriptionPause(option['pause']),
        redeemable: option['redeemable'] === false ? false : undefined,
        way_pass_id: pickString(option['way_pass_id']) || null,
        way_package_name: pickString(option['way_package_name']) || null,
        purchase: readRecord(option['purchase']) ?? null,
        items: Array.isArray(option['items'])
          ? option['items']
              .map((item: unknown) => readRecord(item))
              .filter((item): item is Record<string, any> => Boolean(item))
              .map(item => ({
                purchase_item_id: pickString(item['purchase_item_id']),
                package_id: pickString(item['package_id']),
                wash_type: pickString(item['wash_type']) || null,
                remaining: item['remaining'] == null ? null : Number(item['remaining']),
                limit: item['limit'] == null ? null : Number(item['limit']),
                redeemed_count:
                  item['redeemed_count'] == null ? null : Number(item['redeemed_count']),
                expires_at: pickString(item['expires_at']) || null,
                package_name: pickString(item['package_name']) || null,
              }))
              .filter(item => item.purchase_item_id && item.package_id)
          : undefined,
      } satisfies MultiEligiblePurchaseOption;
    })
    .filter(
      option =>
        isMembershipRedeemEvent(option.redeem_event) ||
        option.redeem_event === 'redeem-prepaid-purchase' ||
        option.redeem_event === 'order-success'
    );
}

export function resolveMultiEligibleSelectionKind(
  data: Record<string, any> | null | undefined,
  options?: MultiEligiblePurchaseOption[]
): MultiEligibleSelectionKind {
  const kind = pickString(data?.['selection_kind']).toLowerCase();
  if (kind === 'memberships' || kind === 'packages') {
    return kind;
  }
  const list = options ?? readMultiEligiblePurchaseOptions(data);
  const membershipCount = list.filter(o => isMembershipRedeemEvent(o.redeem_event)).length;
  const prepaidCount = list.filter(o => o.redeem_event === 'redeem-prepaid-purchase').length;
  return membershipCount > 1 && prepaidCount === 0 ? 'memberships' : 'packages';
}

export function resolveMultiEligiblePlateDisplay(
  data: Record<string, any> | null | undefined
): string {
  return (
    pickString(
      data?.['plate'],
      data?.['vehicle_license_plate'],
      data?.['license_plate'],
      data?.['plateNumber']
    ) || '—'
  );
}

/** US state code carried next to the plate on the LPR payload, or '' when absent / invalid. */
export function resolveMultiEligiblePlateState(
  data: Record<string, any> | null | undefined
): string {
  return coerceUsStateCode(pickString(data?.['state'])) ?? '';
}

/**
 * Plate number for display only: drops the state prefix the LPR payload keeps on the plate
 * (`CA` + `CA8AUG441` → `8AUG441`). Redeem / purchase calls still send the full plate.
 */
export function stripMultiEligiblePlateState(plate: string, state: string): string {
  const code = coerceUsStateCode(state);
  if (code && plate.length > code.length && plate.toUpperCase().startsWith(code)) {
    return plate.slice(code.length);
  }
  return plate;
}

export function resolveMultiEligibleCustomerName(
  data: Record<string, any> | null | undefined
): string {
  const direct = pickString(data?.['customer_name'], data?.['customerName']);
  if (direct) {
    return direct;
  }
  return (
    [data?.['customer_first_name'], data?.['customer_last_name']]
      .map(part => String(part ?? '').trim())
      .filter(Boolean)
      .join(' ') || '—'
  );
}

export function resolveMultiEligibleCustomerPhone(
  data: Record<string, any> | null | undefined
): string {
  return (
    pickString(
      data?.['customer_contact'],
      data?.['customer_phone'],
      data?.['phone'],
      data?.['contact']
    ) || '—'
  );
}

function formatLastWashDisplay(raw: string | null | undefined): string {
  const value = pickString(raw);
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Newest last wash first; missing dates keep relative order at the end. */
export function sortPurchaseOptionsByLastWash(
  options: MultiEligiblePurchaseOption[]
): MultiEligiblePurchaseOption[] {
  return [...options].sort((a, b) => {
    const aTime = new Date(a?.last_washed_date ?? 0).getTime();
    const bTime = new Date(b?.last_washed_date ?? 0).getTime();
    const aOk = Number.isFinite(aTime) ? aTime : 0;
    const bOk = Number.isFinite(bTime) ? bTime : 0;
    return bOk - aOk;
  });
}

export function buildMultiEligibleOptionCards(
  options: MultiEligiblePurchaseOption[]
): MultiEligibleOptionCardVm[] {
  return sortPurchaseOptionsByLastWash(options).map((option, index) => {
    const id =
      pickString(
        option.way_pass_id,
        option.order_id,
        option.prepaid_purchase_id,
        option.subscription_id,
        option.package_id
      ) || `option-${index}`;
    const isMembership = isMembershipRedeemEvent(option.redeem_event);
    const included = option.washes_included;
    const redeemed = option.washes_redeemed ?? 0;
    const remaining =
      included == null || Number.isNaN(included) ? null : Math.max(0, included - redeemed);
    const washesAvailableDisplay =
      remaining == null
        ? isMembership
          ? 'Unlimited'
          : '—'
        : limitRemainingDisplay(remaining, included);
    const washesRedeemedDisplay = `${redeemed} wash${redeemed === 1 ? '' : 'es'} redeemed`;
    const lastWashDisplay = formatLastWashDisplay(option.last_washed_date);
    const availabilityLabel =
      remaining == null && isMembership
        ? 'Unlimited wash'
        : remaining == null
          ? washesAvailableDisplay
          : `${washesAvailableDisplay} left`;
    const lastWashPart = lastWashDisplay === '—' ? null : `Last wash ${lastWashDisplay}`;
    const detailLine = [availabilityLabel, washesRedeemedDisplay, lastWashPart]
      .filter(Boolean)
      .join(' | ');
    const purchaseTypeLabel = isMembership
      ? 'Membership'
      : option.redeem_event === 'order-success'
        ? isWaySingleWashOption(option)
          ? 'Way single wash'
          : 'Single wash'
        : option.purchase_type === 'prepaid'
          ? 'Prepaid'
          : 'Package';
    const statusRaw = pickString(option.status) || 'active';
    // Single washes: badge is the product type (incl. Way), not "Active"/"Scheduled".
    // A pause is not in `status`; the tag is derived from `pause.state` (cardStatus).
    const derivedTag = isMembership
      ? cardStatus({ display_status: statusRaw, is_live: true, pause: option.pause ?? null })
      : null;
    const statusTag =
      derivedTag === 'paused' || derivedTag === 'scheduled_pause' ? derivedTag : null;
    let statusLabel: string;
    if (option.redeem_event === 'order-success') {
      statusLabel = purchaseTypeLabel;
    } else if (statusTag) {
      statusLabel = subscriptionStatusLabel(statusTag);
    } else if (option.reactivated) {
      statusLabel = 'Active · Successfully Reactivated';
    } else {
      statusLabel = statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1).toLowerCase();
    }
    const resumeDate = option.pause?.state === 'paused' ? option.pause.resume_date : null;
    // `resume_date` is a site-local calendar day: parse it locally, not as UTC midnight.
    const resumeDay = toDate(resumeDate);
    const resumesOnDisplay = resumeDay ? formatPauseDate(resumeDay) : null;
    const { isFamily, isPrimary } = familyTagsOf(option);

    return {
      id,
      option,
      packageName: pickString(option.package_name) || 'Package',
      customerName: pickString(option.customer_name) || null,
      washesAvailableDisplay,
      washesRedeemedDisplay,
      lastWashDisplay,
      detailLine,
      statusLabel,
      statusTag,
      resumesOnDisplay,
      isFamily,
      isPrimary,
      orderNumberDisplay: pickString(option.order_number) || '—',
      purchaseTypeLabel,
    };
  });
}

export function isWaySingleWashOption(
  option: MultiEligiblePurchaseOption | null | undefined
): boolean {
  if (!option || option.redeem_event !== 'order-success') {
    return false;
  }
  if (pickString(option.source).toLowerCase() === 'way') {
    return true;
  }
  return Boolean(pickString(option.way_pass_id));
}

/** Map a live Way /passes single wash into a multi-eligible chooser option. */
export function mapWayPurchaseToMultiEligibleOption(
  purchase: Record<string, unknown> | null | undefined
): MultiEligiblePurchaseOption | null {
  const row = readRecord(purchase);
  if (!row) {
    return null;
  }
  const wayPassId = pickString(row['way_pass_id']);
  const purchaseType = pickString(row['purchase_type']).toLowerCase();
  const canRedeem = row['can_redeem'] !== false;
  const status = pickString(row['status']).toLowerCase();
  if (!wayPassId) {
    return null;
  }
  if (purchaseType && purchaseType !== 'single_wash' && purchaseType !== 'single-wash') {
    return null;
  }
  if (status && status !== 'active') {
    return null;
  }
  if (!canRedeem) {
    return null;
  }
  return {
    source: 'way',
    redeem_event: 'order-success',
    purchase_type: 'single_wash',
    way_pass_id: wayPassId,
    way_package_name: pickString(row['package_name']) || null,
    package_id: pickString(row['local_package_id'], row['package_id']) || null,
    package_name: pickString(row['package_name']) || 'Way single wash',
    status: pickString(row['status']) || 'active',
    washes_included: 1,
    washes_redeemed: 0,
    last_washed_date: pickString(row['last_washed_at']) || null,
    purchase: row,
  };
}

const OPTION_CATEGORY_ORDER: MultiEligibleOptionCategoryKey[] = [
  'single_wash',
  'monthly',
  'prepaid',
];

const OPTION_CATEGORY_LABELS: Record<MultiEligibleOptionCategoryKey, string> = {
  single_wash: 'Single wash',
  prepaid: 'Prepaid wash',
  monthly: 'Monthly membership',
};

export function resolveOptionCategoryKey(
  option: MultiEligiblePurchaseOption | null | undefined
): MultiEligibleOptionCategoryKey {
  if (isMembershipRedeemEvent(option?.redeem_event)) {
    return 'monthly';
  }
  if (option?.redeem_event === 'redeem-prepaid-purchase') {
    return 'prepaid';
  }
  return 'single_wash';
}

/**
 * Group chooser cards into Single wash / Monthly membership / Prepaid wash sections.
 * Empty categories are omitted; prepaid is last (Figma Active subscription layout).
 */
export function groupMultiEligibleOptionCards(
  cards: MultiEligibleOptionCardVm[]
): MultiEligibleOptionCategoryVm[] {
  const buckets: Record<MultiEligibleOptionCategoryKey, MultiEligibleOptionCardVm[]> = {
    single_wash: [],
    prepaid: [],
    monthly: [],
  };
  for (const card of cards) {
    buckets[resolveOptionCategoryKey(card.option)].push(card);
  }
  return OPTION_CATEGORY_ORDER.filter(key => buckets[key].length > 0).map(key => ({
    key,
    label: OPTION_CATEGORY_LABELS[key],
    cards: buckets[key],
  }));
}

function limitRemainingDisplay(remaining: number, included: number | null): string {
  if (included == null || Number.isNaN(included)) {
    return String(remaining);
  }
  return `${remaining}/${included}`;
}

export function buildWashItemCards(
  option: MultiEligiblePurchaseOption | null | undefined
): MultiEligibleWashItemCardVm[] {
  return listRedeemablePrepaidItems(option).map((item, index) => {
    const remaining = item.remaining;
    const limit = item.limit;
    const remainingDisplay =
      remaining == null
        ? 'Available'
        : limit != null
          ? `${remaining}/${limit} remaining`
          : `${remaining} remaining`;
    const washLabel = pickString(item.wash_type, item.package_name) || `Wash ${index + 1}`;
    return {
      id: item.purchase_item_id,
      item,
      label: washLabel,
      remainingDisplay,
    };
  });
}

function firstRedeemablePrepaidItem(
  option: MultiEligiblePurchaseOption
): MultiEligiblePrepaidItem | null {
  const [first] = listRedeemablePrepaidItems(option);
  return first ?? null;
}

export function buildMultiEligibleConfirmRequest(
  data: Record<string, any> | null | undefined,
  option: MultiEligiblePurchaseOption,
  selectedItem?: MultiEligiblePrepaidItem | null
): MultiEligibleConfirmRequest | null {
  const row = readRecord(data);
  const lprEntryId = pickString(row?.['lpr_entry_id'], row?.['lprEntryId'], row?.['id']);
  const locationId = pickString(row?.['location_id'], row?.['locationId']);
  const roiId = pickString(row?.['roi_id'], row?.['roiID'], row?.['roiId']);
  const redeemEvent = option.redeem_event;

  if (!row || !lprEntryId || !locationId) {
    return null;
  }

  if (redeemEvent === 'active-subscription') {
    const orderId = pickString(option.order_id);
    if (!orderId) {
      return null;
    }
    const subscriptionId = pickString(option.subscription_id);
    return {
      lpr_entry_id: lprEntryId,
      location_id: locationId,
      redeem_event: 'active-subscription',
      order_id: orderId,
      ...(subscriptionId ? { subscription_id: subscriptionId } : {}),
      ...(roiId ? { roi_id: roiId } : {}),
    };
  }

  if (redeemEvent === 'vendor-subscription') {
    // CWS-1679: no membership order yet; the server redeems by subscription + customer.
    const subscriptionId = pickString(option.subscription_id);
    const customerId = pickString(option.customer_id);
    if (!subscriptionId || !customerId) {
      return null;
    }
    return {
      lpr_entry_id: lprEntryId,
      location_id: locationId,
      redeem_event: 'vendor-subscription',
      subscription_id: subscriptionId,
      customer_id: customerId,
      ...(roiId ? { roi_id: roiId } : {}),
    };
  }

  if (redeemEvent === 'order-success') {
    const orderId = pickString(option.order_id);
    if (!orderId) {
      return null;
    }
    return {
      lpr_entry_id: lprEntryId,
      location_id: locationId,
      redeem_event: 'order-success',
      order_id: orderId,
      ...(roiId ? { roi_id: roiId } : {}),
    };
  }

  const prepaidPurchaseId = pickString(option.prepaid_purchase_id);
  if (!prepaidPurchaseId) {
    return null;
  }

  const item =
    selectedItem && isPrepaidItemRedeemable(selectedItem)
      ? selectedItem
      : firstRedeemablePrepaidItem(option);
  const packageId = pickString(item?.package_id, option.package_id);
  if (!item || !packageId) {
    return null;
  }

  return {
    lpr_entry_id: lprEntryId,
    location_id: locationId,
    redeem_event: 'redeem-prepaid-purchase',
    prepaid_purchase_id: prepaidPurchaseId,
    redemption_items: [
      {
        purchase_item_id: item.purchase_item_id,
        package_id: packageId,
        count: 1,
      },
    ],
    ...(roiId ? { roi_id: roiId } : {}),
  };
}

/** Fields the order-success modal reads (customer strip + package summary). */
export interface OrderSuccessDisplayContext {
  customerFirstName?: string;
  customerLastName?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  packageName?: string;
  plate?: string;
  finalPrice?: string | number | null;
}

export function applyOrderSuccessDisplayFields(
  data: Record<string, any>,
  context: OrderSuccessDisplayContext = {}
): Record<string, any> {
  const out = { ...data };
  const assign = (key: string, value: unknown): void => {
    if (pickString(out[key])) {
      return;
    }
    if (value != null && String(value).trim() !== '') {
      out[key] = value;
    }
  };

  let firstName = pickString(context.customerFirstName);
  let lastName = pickString(context.customerLastName);
  if (!firstName && !lastName) {
    const fullName = pickString(context.customerName);
    if (fullName) {
      const parts = fullName.split(/\s+/).filter(Boolean);
      firstName = parts[0] ?? '';
      lastName = parts.slice(1).join(' ');
    }
  }

  assign('customer_first_name', firstName);
  assign('customer_last_name', lastName);
  assign('customer_email', context.customerEmail);
  assign('customer_contact', context.customerPhone);
  assign('package_name', context.packageName);
  assign('final_price', context.finalPrice ?? out['final_price'] ?? '0');
  assign('plate', context.plate);
  assign('vehicle_license_plate', context.plate);
  return out;
}

export function buildPrepaidRedeemOrderSuccessDisplayContext(params: {
  option: MultiEligiblePurchaseOption;
  selectedItem?: MultiEligiblePrepaidItem | null;
  prepaidStatus?: PrepaidPurchaseStatus | null;
  customerFirstName?: string;
  customerLastName?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  plate?: string;
}): OrderSuccessDisplayContext {
  const packageName =
    pickString(
      params.selectedItem?.package_name,
      params.option.package_name,
      params.prepaidStatus?.book_name
    ) || 'Prepaid wash';
  return {
    customerFirstName: params.customerFirstName,
    customerLastName: params.customerLastName,
    customerName: params.customerName,
    customerEmail: params.customerEmail,
    customerPhone: params.customerPhone,
    packageName,
    plate: params.plate,
    finalPrice: '0',
  };
}

export function buildNotificationFromMultiEligibleConfirmResponse(
  response: any,
  redeemEvent: MultiEligibleRedeemEvent,
  displayContext?: OrderSuccessDisplayContext
): NotificationData | null {
  const envelope = readRecord(response);
  const data = readRecord(envelope?.['data']);
  if (!data) {
    return null;
  }

  const type: NotificationData['type'] = isMembershipRedeemEvent(redeemEvent)
    ? 'active-subscription'
    : 'order-success';
  const message =
    pickString(data['message'], envelope?.['message']) ||
    (type === 'active-subscription'
      ? 'Subscription found. Please proceed to the wash bay.'
      : 'Order completed successfully!');

  let notificationData: Record<string, any> = {
    ...data,
    redemption_completed: true,
    event: type,
  };
  // CWS-1882: a prepaid redemption no longer has an order of its own — when the confirm
  // response carries a visit block, pass it on and point `order_id` at the purchase order.
  const prepaidRedemption = readPrepaidRedemptionVisit(response);
  if (prepaidRedemption) {
    const purchaseOrderId = resolvePrepaidRedemptionOrderId(
      prepaidRedemption,
      pickString(notificationData['order_id'])
    );
    notificationData['prepaid_redemption'] = prepaidRedemption;
    if (purchaseOrderId) {
      notificationData['order_id'] = purchaseOrderId;
    }
  }
  if (type === 'order-success' && displayContext) {
    notificationData = applyOrderSuccessDisplayFields(notificationData, displayContext);
  }

  const reuse = prepaidRedemption ? describePrepaidRedemptionResult(prepaidRedemption) : null;
  const resolvedMessage = reuse?.reused ? reuse.message : message;

  return {
    type,
    message: resolvedMessage,
    timestamp: new Date(),
    data: notificationData,
  };
}

export function resolveMultiEligibleModalTitle(
  _selectionKind: MultiEligibleSelectionKind,
  step?: string
): string {
  if (step === 'phone') {
    return 'Find customer by phone';
  }
  // Figma multi-eligible shells (variants 2–4) keep the Active subscription chrome.
  return 'Active subscription';
}

export function resolveMultiEligibleChooserLabel(
  selectionKind: MultiEligibleSelectionKind,
  step?: string,
  cards?: { option?: { redeem_event?: string | null } | null }[]
): string {
  if (step === 'vehicle') {
    return 'Choose Vehicle';
  }
  if (step === 'wash-item') {
    return 'Select Wash to Redeem';
  }
  if (selectionKind === 'memberships') {
    return 'Choose Membership';
  }
  if (!cards || cards.length === 0) {
    return 'Choose Package';
  }
  const categories = new Set(
    cards.map(card => resolveOptionCategoryKey(card.option as MultiEligiblePurchaseOption))
  );
  if (categories.size > 1) {
    return 'Choose Package';
  }
  if (categories.has('single_wash')) {
    return 'Choose Single Wash';
  }
  if (categories.has('prepaid')) {
    return 'Choose Prepaid Wash';
  }
  if (categories.has('monthly')) {
    return 'Choose Membership';
  }
  return 'Choose Package';
}

export function mapMembershipToPurchaseOption(
  membership: Membership,
  customerId: string,
  vehicleId: string
): MultiEligiblePurchaseOption | null {
  if (membership.type === 'single_wash') {
    const orderId = pickString(membership.order_id);
    if (!orderId) {
      return null;
    }
    // Way passes are not local scheduled orders — redeem via confirm-way / Way modal.
    if (
      pickString(membership.way_pass_id) ||
      pickString(membership.source).toLowerCase() === 'way'
    ) {
      return null;
    }
    return {
      source: 'shop',
      redeem_event: 'order-success',
      purchase_type: 'single_wash',
      order_id: orderId,
      order_number: pickString(membership.order_number) || null,
      package_id: pickString(membership.package_id) || null,
      package_name: pickString(membership.package_name) || 'Single wash',
      customer_id: customerId || null,
      vehicle_id: vehicleId || null,
      status: 'scheduled',
      washes_included: 1,
      washes_redeemed: 0,
      last_washed_date: null,
    };
  }

  if (membership.type === 'subscription') {
    const orderId = pickString(membership.order_id);
    if (!orderId) {
      return null;
    }
    const redeemed = membership.washes_redeemed == null ? 0 : Number(membership.washes_redeemed);
    const included = membership.washes_included == null ? null : Number(membership.washes_included);
    const washesIncluded =
      included == null || Number.isNaN(included) || included === 0 ? null : included;
    const washesRedeemed = Number.isNaN(redeemed) ? 0 : redeemed;
    // Drop limit-exhausted shop memberships (same as prepaid remaining <= 0 / LPR).
    // Way pass orders keep LPR's monthly-limit exception. `source` is not a Way signal:
    // native memberships default to source 'way' too (CWS-1247).
    const isWayMembership = Boolean(pickString(membership.way_pass_id));
    if (!isWayMembership && washesIncluded != null && washesRedeemed >= washesIncluded) {
      return null;
    }
    return {
      source: pickString(membership.source) || 'shop',
      redeem_event: 'active-subscription',
      purchase_type: 'membership',
      order_id: orderId,
      order_number: pickString(membership.order_number) || null,
      package_id: pickString(membership.package_id) || null,
      package_name: pickString(membership.package_name) || 'Membership',
      subscription_id: pickString(membership.id) || null,
      // Phone-lookup memberships carry the subscription's own source (CWS-1426).
      subscription_source: pickString(membership.source) || 'way',
      customer_id: customerId || null,
      vehicle_id: vehicleId || null,
      status: pickString(membership.status) || 'active',
      washes_included: washesIncluded,
      washes_redeemed: washesRedeemed,
      last_washed_date: pickString(membership.last_washed_date) || null,
      pause: membership.pause ?? null,
      redeemable: membership.redeemable === false ? false : undefined,
    };
  }

  if (membership.type === 'prepaid') {
    const prepaidId = pickString(membership.id);
    if (!prepaidId || (membership.remaining_washes ?? 0) <= 0) {
      return null;
    }
    return {
      source: 'shop',
      redeem_event: 'redeem-prepaid-purchase',
      purchase_type: 'prepaid',
      order_id: pickString(membership.order_id) || null,
      order_number: pickString(membership.order_number) || null,
      package_id: pickString(membership.book_id) || null,
      package_name: pickString(membership.book_name) || 'Prepaid',
      prepaid_purchase_id: prepaidId,
      customer_id: customerId || null,
      vehicle_id: vehicleId || null,
      status: 'active',
      washes_included: membership.total_items ?? null,
      washes_redeemed: membership.redeemed_count ?? 0,
      last_washed_date: null,
      items: undefined,
    };
  }

  return null;
}

/**
 * Phone entry → Active subscription when the customer must choose among options:
 * multiple plates with packages, multiple packages on a single plate, or
 * local package(s) + live Way single wash(es) that will merge into the same chooser.
 *
 * A lone prepaid wash book counts on its own. There is no *choice* to make there, but the
 * book still has to be opened to redeem from: which wash of the several it holds, on which
 * vehicle. Falling through left that happening inside the order form instead, with the
 * redemption panel and the purchase catalog stacked on one screen.
 */
export function shouldOpenPhoneActiveSubscriptionLookup(
  vehicleCards: MultiEligibleVehicleCardVm[],
  waySingleWashCount = 0
): boolean {
  const wayCount = Math.max(0, Number(waySingleWashCount) || 0);
  if (vehicleCards.length >= 2) {
    return true;
  }
  if (vehicleCards.length === 1) {
    if (vehicleCards[0].memberships.some(optionIsPrepaidPurchase)) {
      return true;
    }
    return vehicleCards[0].memberships.length + wayCount >= 2;
  }
  // No local packages — Way-only stays on the create-order / Way modal path.
  return false;
}

/** Count live Way purchases that map into multi-eligible single-wash cards. */
export function countRedeemableWaySingleWashPurchases(
  purchases: readonly unknown[] | null | undefined
): number {
  if (!Array.isArray(purchases)) {
    return 0;
  }
  return purchases
    .map(purchase => mapWayPurchaseToMultiEligibleOption(purchase as Record<string, unknown>))
    .filter((option): option is MultiEligiblePurchaseOption => Boolean(option)).length;
}

export function buildVehicleCardsFromPhoneResponse(
  response: VehiclesWithMembershipsResponse | null | undefined
): MultiEligibleVehicleCardVm[] {
  const customerId = pickString(response?.customer?.id);
  const vehicles = response?.vehicles ?? [];
  return vehicles
    .map((entry, index) => {
      const vehicleId = pickString(entry.vehicle?.vehicle_id);
      const ownerId = pickString(entry.vehicle?.customer_id) || customerId;
      const memberships = (entry.memberships ?? [])
        .map(m => mapMembershipToPurchaseOption(m, ownerId, vehicleId))
        .filter((m): m is MultiEligiblePurchaseOption => Boolean(m));
      if (memberships.length === 0) {
        return null;
      }
      const plate = pickString(entry.vehicle?.license_plate) || '—';
      const detailParts = [
        entry.vehicle?.year != null ? String(entry.vehicle.year) : '',
        pickString(entry.vehicle?.make),
        pickString(entry.vehicle?.model),
      ].filter(Boolean);
      return {
        id: vehicleId || `vehicle-${index}`,
        licensePlate: plate,
        detailLine: detailParts.join(' ') || 'Vehicle',
        membershipCount: memberships.length,
        memberships,
      } satisfies MultiEligibleVehicleCardVm;
    })
    .filter((card): card is MultiEligibleVehicleCardVm => Boolean(card));
}

/** Map prepaid purchase status items onto a multi-eligible prepaid option. */
export function attachPrepaidStatusItemsToOption(
  option: MultiEligiblePurchaseOption,
  status: PrepaidPurchaseStatus | null | undefined
): MultiEligiblePurchaseOption {
  const items = (status?.items ?? [])
    .map((raw): MultiEligiblePrepaidItem | null => {
      const purchaseItemId = pickString(raw.purchase_item_id, raw.id);
      const packageId = pickString(raw.package_id);
      if (!purchaseItemId || !packageId) {
        return null;
      }
      const limit = raw.wash_limit ?? raw.limit ?? null;
      const redeemed = raw.redeemed_count ?? 0;
      const remaining =
        raw.remaining != null
          ? Number(raw.remaining)
          : limit != null
            ? Math.max(0, Number(limit) - Number(redeemed))
            : null;
      return {
        purchase_item_id: purchaseItemId,
        package_id: packageId,
        wash_type: pickString(raw.type) || null,
        remaining,
        limit: limit == null ? null : Number(limit),
        redeemed_count: Number(redeemed) || 0,
        expires_at: pickString(raw.expires_at) || null,
        package_name: pickString(raw.package_name) || null,
      };
    })
    .filter((item): item is MultiEligiblePrepaidItem => item != null);

  return {
    ...option,
    package_name: pickString(status?.book_name, option.package_name) || option.package_name,
    items,
  };
}

/** Figma washbook header: "Prepaid Washbook: 2 Single Wash Champion, 1 Add-on Interior". */
export function buildPrepaidWashbookTitle(
  status: PrepaidPurchaseStatus | null | undefined
): string {
  const book = pickString(status?.book_name) || 'Prepaid Washbook';
  const parts = (status?.items ?? []).map(item => {
    const limit =
      item.wash_limit != null
        ? Number(item.wash_limit)
        : item.limit != null
          ? Number(item.limit)
          : 0;
    const typeRaw = pickString(item.type) || (item.is_add_on ? 'add_on' : 'single_wash');
    const typeLabel =
      typeRaw === 'add_on'
        ? 'Add-on'
        : typeRaw === 'single_wash'
          ? 'Single Wash'
          : typeRaw.replace(/_/g, ' ');
    const name =
      [pickString(item.package_name), pickString(item.variant_name)].filter(Boolean).join(' - ') ||
      typeLabel;
    const count = Number.isFinite(limit) && limit > 0 ? limit : (item.remaining ?? 0);
    return `${count} ${typeLabel} ${name}`.replace(/\s+/g, ' ').trim();
  });
  if (parts.length === 0) {
    return book;
  }
  return `${book}: ${parts.join(', ')}`;
}

export function buildPhoneMembershipRedeemPayload(params: {
  option: MultiEligiblePurchaseOption;
  locationId: string;
  laneId?: string | null;
  timezone?: string;
}): Record<string, unknown> | null {
  const orderId = pickString(params.option.order_id);
  const locationId = pickString(params.locationId);
  const vehicleId = pickString(params.option.vehicle_id);
  const customerId = pickString(params.option.customer_id);
  const subscriptionId = pickString(params.option.subscription_id);
  // Backend resolves package_id from the membership order; only core ids are required here.
  if (!orderId || !locationId || !vehicleId || !customerId) {
    return null;
  }
  const laneId = pickString(params.laneId);
  return {
    order_id: orderId,
    location_id: locationId,
    vehicle_id: vehicleId,
    customer_id: customerId,
    ...(subscriptionId ? { subscription_id: subscriptionId } : {}),
    ...(laneId ? { lane_id: laneId } : {}),
    timezone: params.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  };
}

/** Same identity fields as membership redeem; targets phone-single-wash-redeem. */
export function buildPhoneSingleWashRedeemPayload(params: {
  option: MultiEligiblePurchaseOption;
  locationId: string;
  laneId?: string | null;
  timezone?: string;
}): Record<string, unknown> | null {
  return buildPhoneMembershipRedeemPayload(params);
}

export function buildPhoneMembershipRedeemSuccessNotification(
  option: MultiEligiblePurchaseOption,
  context: {
    plate?: string;
    customerName?: string;
    customerPhone?: string;
    locationId?: string;
    laneId?: string;
  }
): NotificationData {
  return {
    type: 'active-subscription',
    message: 'Membership wash redeemed. Please proceed to the wash bay.',
    timestamp: new Date(),
    data: {
      event: 'active-subscription',
      order_id: option.order_id,
      subscription_id: option.subscription_id,
      package_id: option.package_id,
      package_name: option.package_name,
      customer_id: option.customer_id,
      vehicle_id: option.vehicle_id,
      plate: context.plate,
      vehicle_license_plate: context.plate,
      customer_name: context.customerName,
      customer_contact: context.customerPhone,
      location_id: context.locationId,
      ...(context.laneId ? { lane_id: context.laneId } : {}),
      total_washes_available: option.washes_included,
      total_washes_redeemed: option.washes_redeemed,
      last_washed_date: option.last_washed_date,
      status: option.status ?? 'active',
      redemption_completed: true,
      phone_membership_lookup: true,
    },
  };
}

export function buildPhonePrepaidOrderPayload(params: {
  customerId: string;
  vehicleId: string;
  locationId: string;
  prepaidPurchaseId: string;
  item: MultiEligiblePrepaidItem;
  laneId?: string | null;
}): Record<string, any> | null {
  const customerId = pickString(params.customerId);
  const vehicleId = pickString(params.vehicleId);
  const locationId = pickString(params.locationId);
  const prepaidPurchaseId = pickString(params.prepaidPurchaseId);
  const purchaseItemId = pickString(params.item.purchase_item_id);
  const packageId = pickString(params.item.package_id);
  if (
    !customerId ||
    !vehicleId ||
    !locationId ||
    !prepaidPurchaseId ||
    !purchaseItemId ||
    !packageId
  ) {
    return null;
  }
  const laneId = pickString(params.laneId);
  return {
    customer_id: customerId,
    vehicle_id: vehicleId,
    location_id: locationId,
    wash_type: 'prepaid_wash_redemption',
    prepaid_purchase_id: prepaidPurchaseId,
    redemption_items: [{ purchase_item_id: purchaseItemId, package_id: packageId, count: 1 }],
    appointment_time: new Date().toISOString(),
    source: 'web',
    order_source: 'saas',
    final_price: '0',
    ...(laneId ? { lane_id: laneId } : {}),
  };
}

export function buildActiveSubscriptionNotificationFromOption(
  option: MultiEligiblePurchaseOption,
  context: {
    plate?: string;
    customerName?: string;
    customerPhone?: string;
    locationId?: string;
    laneId?: string;
  }
): NotificationData {
  return {
    type: 'active-subscription',
    message: 'Subscription found. Please proceed to the wash bay.',
    timestamp: new Date(),
    data: {
      event: 'active-subscription',
      order_id: option.order_id,
      subscription_id: option.subscription_id,
      package_id: option.package_id,
      package_name: option.package_name,
      customer_id: option.customer_id,
      vehicle_id: option.vehicle_id,
      plate: context.plate,
      vehicle_license_plate: context.plate,
      customer_name: context.customerName,
      customer_contact: context.customerPhone,
      location_id: context.locationId,
      ...(context.laneId ? { lane_id: context.laneId } : {}),
      total_washes_available: option.washes_included,
      total_washes_redeemed: option.washes_redeemed,
      last_washed_date: option.last_washed_date,
      status: option.status ?? 'active',
      phone_membership_lookup: true,
    },
  };
}

/**
 * CWS-1882: the redemption response's `data.order` is the PURCHASE order and carries the
 * `prepaid_redemption` visit block. Read it from wherever it sits so the success modal and
 * anything downstream get the purchase order id and the reuse flag instead of guessing.
 */
export function readPrepaidRedemptionVisit(response: any): PrepaidRedemptionVisitLike | null {
  const envelope = readRecord(response);
  const data = readRecord(envelope?.['data']) ?? envelope;
  return (
    readRecord(readRecord(data?.['order'])?.['prepaid_redemption']) ??
    readRecord(data?.['prepaid_redemption'])
  );
}

export function buildOrderSuccessNotificationFromPhonePrepaid(
  response: any,
  displayContext: OrderSuccessDisplayContext = {}
): NotificationData | null {
  const envelope = readRecord(response);
  const data = readRecord(envelope?.['data']) ?? readRecord(envelope);
  if (!data) {
    return null;
  }
  const plate = pickString(data['plate'], displayContext.plate);
  const prepaidRedemption = readPrepaidRedemptionVisit(response);
  // A reused visit drew no extra wash, so the API's "redeemed" message would be wrong.
  const redemptionResult = describePrepaidRedemptionResult(prepaidRedemption);
  const orderId = resolvePrepaidRedemptionOrderId(
    prepaidRedemption,
    pickString(data['order_id'], readRecord(data['order'])?.['id'])
  );
  const message = redemptionResult.reused
    ? redemptionResult.message
    : pickString(data['message'], envelope?.['message']) ||
      'Prepaid wash redeemed. Please proceed to the wash bay.';
  return {
    type: 'order-success',
    message,
    timestamp: new Date(),
    data: applyOrderSuccessDisplayFields(
      {
        ...data,
        ...(orderId ? { order_id: orderId } : {}),
        ...(prepaidRedemption ? { prepaid_redemption: prepaidRedemption } : {}),
        plate: plate || undefined,
        redemption_completed: true,
        event: 'order-success',
      },
      displayContext
    ),
  };
}

/** @internal test helper — keep VehicleWithMemberships typing available for callers */
export type { VehicleWithMemberships };

/** CWS-1882 visit block shape, re-exported so callers type the notification payload. */
export type { PrepaidRedemptionVisitLike };
